import { Component, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { AbstractCellEditor, CustomCellEditorParams } from '@app/shared/components/cell-edit/abstract.cell-editor';
import { merge, Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'text-cell-edit',
  templateUrl: './text-cell-edit.component.html',
  styleUrls: ['./text-cell-edit.component.scss'],
  standalone: false
})
export class TextCellEditComponent extends AbstractCellEditor<string> implements OnDestroy {
  public suffix?: string;
  public suggestedOptions?: string[];
  public filteredSuggestions: Observable<string[]>;

  @ViewChild(MatInput, {static: false}) public input: MatInput;
  @ViewChild('auto', {static: false}) public autocomplete: MatAutocomplete;

  /**
   * Initialisation method for the render component
   * @param params A merge of default cell editor params and custom params
   */
  public agInit(params: CustomCellEditorParams<string>): void {
    super.agInit(params);
    this.suffix = this.params.suffix;
    this.suggestedOptions = this.params.suggestedOptions;

    if (this.suggestedOptions) {
      this.filteredSuggestions = this.control.valueChanges
        .pipe(
          startWith(''),
          map(value => this.filter(value))
        );
    }
  }

  /**
   *  A hook to perform any necessary operation just after the GUI for this component has been rendered on the screen.
   *  This method is called each time the edit component is activated.
   */
  public afterGuiAttached(): void {
    if (!this.fullRowEdit) {
      this.input.focus();
    }
  }

  /**
   * Gets called when focus should be put into the editor.
   */
  public focusIn(): void {
    this.input.focus();
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.suggestedOptions.filter(option => option.toLowerCase().includes(filterValue));
  }

  public optionSelected(): void {
    this.params.stopEditing();
  }

  public onFocusOut(): void {
    if (this.autocomplete.isOpen) {
      //Perform the save only if no options was selected
      merge(
        this.autocomplete.optionSelected.asObservable().pipe(map(() => true)),
        this.autocomplete.closed.asObservable()
      )
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          if (!res) this.params.stopEditing();
        });
    }
  }

  /**
   *  Remove the error class on the cell GUI and stop Observable subscriptions.
   */
  public ngOnDestroy(): void {
    this.params.eGridCell.classList.remove('pending-cell');
    this.params.eGridCell.classList.remove('error-cell');
  }
}
